'use client';

import { Focus, Maximize2, Minus, Network, Plus } from 'lucide-react';
import type { AtlasEdge, AtlasNode, NodeType } from './types';

const TYPE_STYLE: Record<NodeType, { border: string; badge: string; label: string }> = {
  capability: { border: '#2563eb', badge: '#dbeafe', label: 'Capability' },
  application: { border: '#0891b2', badge: '#cffafe', label: 'Application' },
  service: { border: '#0e7490', badge: '#cffafe', label: 'Service' },
  data_store: { border: '#7c3aed', badge: '#ede9fe', label: 'Data store' },
  identity: { border: '#d97706', badge: '#fef3c7', label: 'Identity provider' },
  infrastructure: { border: '#475569', badge: '#e2e8f0', label: 'Infrastructure' },
  vendor: { border: '#ea580c', badge: '#ffedd5', label: 'Vendor' },
  procedure: { border: '#16a34a', badge: '#dcfce7', label: 'Procedure' },
  role: { border: '#0f766e', badge: '#ccfbf1', label: 'Role / team' },
  control: { border: '#ca8a04', badge: '#fef9c3', label: 'Control' },
};

interface GraphCanvasProps {
  nodes: AtlasNode[];
  edges: AtlasEdge[];
  selectedId: string | null;
  impactNodeIds: Set<string>;
  impactEdgeIds: Set<string>;
  zoom: number;
  onZoom: (zoom: number) => void;
  onSelect: (id: string) => void;
  onAddNode: () => void;
  onAutoLayout: () => void;
}

export default function GraphCanvas({
  nodes,
  edges,
  selectedId,
  impactNodeIds,
  impactEdgeIds,
  zoom,
  onZoom,
  onSelect,
  onAddNode,
  onAutoLayout,
}: GraphCanvasProps) {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const hasImpact = impactNodeIds.size > 0;
  const viewWidth = 1040 / zoom;
  const viewHeight = 610 / zoom;
  const selectedNode = selectedId ? byId.get(selectedId) : undefined;
  const centerX = selectedNode ? selectedNode.x + 89 : 520;
  const centerY = selectedNode ? selectedNode.y + 35 : 305;
  const offsetX = Math.max(0, Math.min(1040 - viewWidth, centerX - viewWidth / 2));
  const offsetY = Math.max(0, Math.min(610 - viewHeight, centerY - viewHeight / 2));

  return (
    <div className="relative h-full min-h-[560px] overflow-hidden bg-[#f7f8f6] dark:bg-slate-950">
      <div className="pointer-events-none absolute inset-0 opacity-50 dark:opacity-15" aria-hidden="true"
        style={{ backgroundImage: 'radial-gradient(#94a3b8 0.7px, transparent 0.7px)', backgroundSize: '18px 18px' }} />

      <div className="absolute left-4 top-4 z-10 flex items-center gap-2">
        <button type="button" onClick={onAddNode}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:border-cyan-400 hover:text-cyan-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
          <Plus className="h-4 w-4" /> Add entity
        </button>
        {hasImpact && (
          <span className="rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 dark:border-red-900 dark:bg-red-950/70 dark:text-red-300">
            Impact path highlighted
          </span>
        )}
      </div>

      <svg className="relative h-full min-h-[560px] w-full" viewBox={`${offsetX} ${offsetY} ${viewWidth} ${viewHeight}`}
        aria-label="Interactive dependency graph">
        <defs>
          <marker id="atlas-arrow" markerWidth="8" markerHeight="8" refX="7" refY="3.5" orient="auto">
            <path d="M0,0 L7,3.5 L0,7 Z" fill="#94a3b8" />
          </marker>
          <marker id="atlas-arrow-impact" markerWidth="8" markerHeight="8" refX="7" refY="3.5" orient="auto">
            <path d="M0,0 L7,3.5 L0,7 Z" fill="#ef4444" />
          </marker>
        </defs>

        {edges.map((edge) => {
          const source = byId.get(edge.sourceId);
          const target = byId.get(edge.targetId);
          if (!source || !target) return null;
          const highlighted = impactEdgeIds.has(edge.id);
          const muted = hasImpact && !highlighted;
          const x1 = source.x + 89;
          const y1 = source.y + 34;
          const x2 = target.x + 89;
          const y2 = target.y + 34;
          const curve = Math.max(30, Math.abs(x2 - x1) * 0.35);
          const path = `M ${x1} ${y1} C ${x1 + curve} ${y1}, ${x2 - curve} ${y2}, ${x2} ${y2}`;
          return (
            <g key={edge.id} opacity={muted ? 0.13 : 1}>
              <path d={path} fill="none" stroke={highlighted ? '#ef4444' : '#94a3b8'}
                strokeWidth={highlighted ? 3 : 1.5} strokeDasharray={edge.confidence === 'inferred' ? '6 5' : undefined}
                markerEnd={highlighted ? 'url(#atlas-arrow-impact)' : 'url(#atlas-arrow)'} />
              {(highlighted || selectedId === edge.sourceId || selectedId === edge.targetId) && (
                <g>
                  <rect x={(x1 + x2) / 2 - 55} y={(y1 + y2) / 2 - 10} width="110" height="20" rx="10"
                    fill={highlighted ? '#fff1f2' : '#ffffff'} stroke={highlighted ? '#fecdd3' : '#e2e8f0'} />
                  <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 + 3.5} textAnchor="middle"
                    fontSize="9" fontWeight="700" fill={highlighted ? '#be123c' : '#475569'}>
                    {edge.type.replaceAll('_', ' ')}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {nodes.map((node) => {
          const style = TYPE_STYLE[node.type];
          const selected = node.id === selectedId;
          const impacted = impactNodeIds.has(node.id);
          const muted = hasImpact && !impacted;
          return (
            <foreignObject key={node.id} x={node.x} y={node.y} width="178" height="70" opacity={muted ? 0.18 : 1}>
              <button type="button" onClick={() => onSelect(node.id)}
                className="group h-[66px] w-[174px] overflow-hidden rounded-xl bg-white px-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-cyan-500 dark:bg-slate-900"
                style={{ border: `${selected ? 3 : impacted ? 2.5 : 1.5}px solid ${impacted ? '#ef4444' : style.border}` }}
                aria-label={`${node.name}, ${style.label}, criticality ${node.criticality}`}>
                <span className="flex items-center gap-2">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[10px] font-black uppercase text-slate-700 dark:text-slate-200"
                    style={{ backgroundColor: impacted ? '#ffe4e6' : style.badge }}>
                    {node.type === 'data_store' ? 'DB' : node.name.split(' ').slice(0, 2).map((word) => word[0]).join('')}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] font-bold text-slate-900 dark:text-slate-100">{node.name}</span>
                    <span className="block truncate text-[9px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{style.label}</span>
                  </span>
                  <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-[9px] font-bold ${node.criticality >= 5 ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300' : node.criticality >= 4 ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>{node.criticality}</span>
                </span>
              </button>
            </foreignObject>
          );
        })}
      </svg>

      <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900">
        <button type="button" onClick={() => onZoom(Math.max(0.7, +(zoom - 0.1).toFixed(1)))} className="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Zoom out"><Minus className="h-4 w-4" /></button>
        <span className="w-12 text-center text-[10px] font-semibold text-slate-500">{Math.round(zoom * 100)}%</span>
        <button type="button" onClick={() => onZoom(Math.min(1.6, +(zoom + 0.1).toFixed(1)))} className="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label="Zoom in"><Plus className="h-4 w-4" /></button>
        <button type="button" onClick={() => onZoom(1)} className="border-l border-slate-200 p-2 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800" aria-label="Fit graph"><Maximize2 className="h-4 w-4" /></button>
        <button type="button" onClick={onAutoLayout} className="border-l border-slate-200 p-2 text-slate-500 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800" aria-label="Automatic layout"><Network className="h-4 w-4" /></button>
        <button type="button" onClick={() => selectedId && onZoom(Math.max(1.2, zoom))} disabled={!selectedId} className="border-l border-slate-200 p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30 dark:border-slate-700 dark:hover:bg-slate-800" aria-label="Focus selected entity"><Focus className="h-4 w-4" /></button>
      </div>
    </div>
  );
}
